import { Box } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, type ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { nurseRemovalTableSortingKey } from "../../../services/LocalStorageService";
import { STAFF_PICKER } from "../../../theme/tokens";
import type { NurseData } from "../../../utils/models";
import { formatShiftDate } from "../../AddNurse/addStaffUtils";
import type { NursesMatrixSelectionMode } from "../../NursesMatrixGrid/consts";
import { NursesMatrixGrid } from "../../NursesMatrixGrid/NursesMatrixGrid";
import { EmptyState } from "../../UIToolkit/molecules/EmptyState";
import type { RowGroupingOptions } from "../../UIToolkit/Tables/SystemDataGrid/types";
import { bandAriaLabel, RemoveStaffBandLabel } from "./RemoveStaffBandLabel";
import { filterTestIdSegment } from "./RemoveStaffWindowFilter";
import type { RemoveStaffRow } from "./removeStaffRows";
import { REMOVE_STAFF_ALL_FILTER } from "./removeStaffUtils";
import { REMOVE_STAFF_OTHER_WINDOW_KEY, type RemoveStaffShiftPlacement, type RemoveStaffWindow } from "./removeStaffWindows";
import { windowKeyOfRow } from "./useRemoveStaffFilter";

/** The search takes the toolbar's spare room between these widths, like Add Staff's. */
const SEARCH_WIDTH_RANGE = { min: STAFF_PICKER.SEARCH_MIN_WIDTH, max: STAFF_PICKER.SEARCH_MAX_WIDTH } as const;

/** The grid sizes to its rows up to the cap, then scrolls — the popup around it is content-sized. */
const CONTAINED_HEIGHT = { maxHeight: STAFF_PICKER.REMOVE_GRID_MAX_HEIGHT } as const;

type Props = {
	rows: RemoveStaffRow[];
	windows: readonly RemoveStaffWindow[];
	placementByShiftId: ReadonlyMap<string, RemoveStaffShiftPlacement>;
	activeFilter: string;
	dateRange: DateTime[];
	dayNo: number;
	staffingDate: DateTime;
	selectionMode: NursesMatrixSelectionMode;
	selectedRowIds: string[];
	onSelectionChange: (rows: NurseData[]) => void;
	loading: boolean;
	/** The window pills, at the start of the grid's toolbar row. */
	filters: ReactNode;
};

/**
 * One band per window, in the department's day order, then Other. Under "All shifts" every staffed window keeps
 * its band even with nobody on it, so the gap reads "No one scheduled" instead of the window vanishing.
 */
const useWindowGrouping = (
	windows: readonly RemoveStaffWindow[],
	placementByShiftId: ReadonlyMap<string, RemoveStaffShiftPlacement>,
	activeFilter: string,
): RowGroupingOptions<NurseData> => {
	const { t } = useTranslation();
	return useMemo(() => {
		const windowByKey = new Map(windows.map((window) => [window.key, window]));
		// DEFAULT: a key with no window is the Other band, which has no hours of its own.
		const windowOf = (key: string) => windowByKey.get(key) ?? null;
		const staffedKeys = windows.filter((window) => window.isStaffed).map((window) => window.key);
		return {
			getGroupKey: (row) => windowKeyOfRow(row, placementByShiftId),
			groupOrder: [...windows.map((window) => window.key), REMOVE_STAFF_OTHER_WINDOW_KEY],
			persistentGroupKeys: activeFilter === REMOVE_STAFF_ALL_FILTER ? staffedKeys : [],
			collapsible: true,
			renderGroupLabel: (group) => (
				<RemoveStaffBandLabel window={windowOf(group.key)} count={group.rows.length} dataTestId={`remove-staff-band-${filterTestIdSegment(group.key)}`} />
			),
			getGroupAriaLabel: (group) => bandAriaLabel(windowOf(group.key), group.rows.length, t),
		};
	}, [windows, placementByShiftId, activeFilter, t]);
};

/**
 * Every shift on the date, grouped by time window, as one picker: tick one or several (radio in single mode).
 * Rows keep the matrix's columns — role, seniority, removals, cost, the week — and name cells keep their
 * `nurses-matrix-grid-row-{nurseId}` test id.
 */
export const RemoveStaffGrid: FC<Props> = (props) => {
	const { t } = useTranslation();
	const rowGrouping = useWindowGrouping(props.windows, props.placementByShiftId, props.activeFilter);
	return (
		<Box sx={{ display: "flex", flexDirection: "column" }} data-testid="remove-staff-grid">
			<NursesMatrixGrid
				nursesData={props.rows}
				dateRange={props.dateRange}
				highlightDateIndexes={[props.dayNo]}
				isAdd={false}
				filterDate={props.staffingDate}
				localStorageTableKey={nurseRemovalTableSortingKey}
				enableColumnPicker
				loading={props.loading}
				emptyState={
					<EmptyState
						title={t("remove.staff.empty", { date: formatShiftDate(props.staffingDate) })}
						hint={t("remove.staff.empty.hint")}
						dataTestId="remove-staff-empty"
					/>
				}
				searchableColumns={["name"]}
				searchPlaceholder={t("add.staff.search")}
				searchWidth={SEARCH_WIDTH_RANGE}
				leadingToolbarNodes={props.filters}
				selectionMode={props.selectionMode}
				selectedRowIds={props.selectedRowIds}
				onSelectionChange={props.onSelectionChange}
				rowGrouping={rowGrouping}
				containedHeight={CONTAINED_HEIGHT}
			/>
		</Box>
	);
};
